// ===== ChatHistorySidebar.js — UPDATED with delete button =====

import React, { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';

const ChatHistorySidebar = ({
  isOpen,
  onClose,
  sessionId,
  userName,
  sessions,
  isLoading,
  onRequestSessions,
  onSelectSession,
  onDeleteSession,
}) => {
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (isOpen && !loadedOnce && onRequestSessions) {
      onRequestSessions();
      setLoadedOnce(true);
    }
  }, [isOpen, loadedOnce, onRequestSessions]);

  useEffect(() => {
    if (!isOpen) {
      setLoadedOnce(false);
      setConfirmDeleteId(null);
    }
  }, [isOpen]);

  const handleRefresh = useCallback(() => {
    if (onRequestSessions) onRequestSessions();
  }, [onRequestSessions]);

  const handleSelect = useCallback(
    (sid) => {
      if (confirmDeleteId) {
        setConfirmDeleteId(null);
        return;
      }
      if (onSelectSession) onSelectSession(sid);
    },
    [onSelectSession, confirmDeleteId]
  );

  const handleDeleteClick = useCallback((e, sid) => {
    e.stopPropagation();
    if (confirmDeleteId === sid) {
      // Second click — confirm delete
      setDeletingSessionId(sid);
      setConfirmDeleteId(null);
      if (onDeleteSession) {
        onDeleteSession(sid);
        // Remove from local view after a short delay
        setTimeout(() => {
          setDeletingSessionId(null);
          // Refresh the list
          if (onRequestSessions) onRequestSessions();
        }, 1000);
      }
    } else {
      // First click — show confirm
      setConfirmDeleteId(sid);
      // Auto-cancel confirm after 3 seconds
      setTimeout(() => {
        setConfirmDeleteId(prev => prev === sid ? null : prev);
      }, 3000);
    }
  }, [confirmDeleteId, onDeleteSession, onRequestSessions]);

  const formatDate = (timestamp) => {
      if (!timestamp) return '';
      try {
          const date = new Date(timestamp);
          if (isNaN(date.getTime())) return '';
          return formatDistanceToNow(date, { addSuffix: true });
      } catch {
          return '';
      }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/25 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed top-0 left-0 h-full bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 z-50 flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: '360px', maxWidth: '85vw' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Chat History
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Refresh */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/30 transition-colors"
          >
            {isLoading ? (
              <>
                <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading…
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </>
            )}
          </button>
        </div>

        {/* Session list */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (!sessions || sessions.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Loading chat sessions…
              </span>
            </div>
          ) : !sessions || sessions.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No previous chats found
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Start a conversation and it will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map((session, i) => {
                const isActive = session.sessionId === sessionId;
                const isDeleting = deletingSessionId === session.sessionId;
                const isConfirming = confirmDeleteId === session.sessionId;

                if (isDeleting) {
                  return (
                    <div
                      key={session.sessionId || i}
                      className="w-full p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 animate-pulse"
                    >
                      <span className="text-sm text-red-600 dark:text-red-400">Deleting…</span>
                    </div>
                  );
                }

                return (
                  <div
                    key={session.sessionId || i}
                    className={`relative w-full text-left p-3 rounded-lg border transition-all duration-200 group ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 ring-2 ring-blue-500/20'
                        : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    {/* Clickable area */}
                    <button
                      onClick={() => handleSelect(session.sessionId)}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between mb-1 pr-8">
                        <span
                          className={`text-sm font-medium truncate flex-1 mr-2 ${
                            isActive
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400'
                          }`}
                        >
                          {session.summary ||
                            session.firstMessage ||
                            session.sessionId?.slice(0, 20) + '…'}
                        </span>
                        {isActive && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 flex-shrink-0">
                            Active
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {session.messageCount
                            ? `${session.messageCount} messages`
                            : ''}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {session.lastModified
                            ? formatDate(session.lastModified)
                            : session.lastActivityStr
                            ? formatDate(session.lastActivityStr)
                            : ''}
                        </span>
                      </div>
                    </button>

                    {/* Delete button */}
                    <button
                      onClick={(e) => handleDeleteClick(e, session.sessionId)}
                      className={`absolute top-2.5 right-2.5 p-1.5 rounded-md transition-all duration-200 ${
                        isConfirming
                          ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 opacity-100 scale-110'
                          : 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100'
                      }`}
                      title={isConfirming ? 'Click again to confirm delete' : 'Delete chat'}
                    >
                      {isConfirming ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatHistorySidebar;